import React, { useState } from 'react';
import { Upload, Button, Select, Space, Table, message, Card, Typography, Row, Col } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import styled from 'styled-components';

const { Option } = Select;
const { Title, Text } = Typography;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UploadSection = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  width: 100%;
`;

interface FileData {
  sheets: string[];
  columns: string[];
  data: any[];
  fileName: string;
}

interface SelectedColumns {
  file1Column: string;
  file2Column: string;
}

const ExcelMerger: React.FC = () => {
  const [file1Data, setFile1Data] = useState<FileData | null>(null);
  const [file2Data, setFile2Data] = useState<FileData | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<SelectedColumns>({
    file1Column: '',
    file2Column: ''
  });
  const [mergedData, setMergedData] = useState<any[]>([]);

  const handleFileRead = (file: File, setFileData: (data: FileData) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet);
      const columns = Object.keys(jsonData[0] || {});

      setFileData({
        sheets: workbook.SheetNames,
        columns,
        data: jsonData,
        fileName: file.name
      });
    };
    reader.readAsArrayBuffer(file);
  };

  const handleMerge = () => {
    if (!file1Data || !file2Data || !selectedColumns.file1Column || !selectedColumns.file2Column) {
      message.error('Please select columns from both files to merge');
      return;
    }

    const merged = file1Data.data.map(row1 => {
      const matchingRow = file2Data.data.find(
        row2 => row2[selectedColumns.file2Column] === row1[selectedColumns.file1Column]
      );
      return { ...row1, ...matchingRow };
    });

    setMergedData(merged);
    message.success('Files merged successfully!');
  };

  const handleDownload = () => {
    if (mergedData.length === 0) {
      message.error('No merged data to download');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(mergedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Merged');
    XLSX.writeFile(wb, 'merged.xlsx');
  };

  const handleColumnSelect = (value: string, fileNumber: 1 | 2) => {
    setSelectedColumns(prev => ({
      ...prev,
      [fileNumber === 1 ? 'file1Column' : 'file2Column']: value
    }));
  };

  return (
    <Container>
      <Title level={4}>Upload Excel Files to Merge</Title>
      
      <UploadSection>
        <StyledCard title="First Excel File" size="small">
          <Upload
            beforeUpload={(file) => {
              handleFileRead(file, setFile1Data);
              return false;
            }}
            accept=".xlsx,.xls"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Upload First File</Button>
          </Upload>
          {file1Data && (
            <Text type="success" style={{ display: 'block', marginTop: '8px' }}>
              Uploaded: {file1Data.fileName}
            </Text>
          )}
        </StyledCard>

        <StyledCard title="Second Excel File" size="small">
          <Upload
            beforeUpload={(file) => {
              handleFileRead(file, setFile2Data);
              return false;
            }}
            accept=".xlsx,.xls"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Upload Second File</Button>
          </Upload>
          {file2Data && (
            <Text type="success" style={{ display: 'block', marginTop: '8px' }}>
              Uploaded: {file2Data.fileName}
            </Text>
          )}
        </StyledCard>
      </UploadSection>

      {file1Data && file2Data && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <StyledCard title="Select Columns to Match" size="small">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Select column from first file:</Text>
                  <Text type="secondary">({file1Data.fileName})</Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select column from first file"
                    onChange={(value) => handleColumnSelect(value, 1)}
                    value={selectedColumns.file1Column}
                  >
                    {file1Data.columns.map(col => (
                      <Option key={col} value={col}>{col}</Option>
                    ))}
                  </Select>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Select column from second file:</Text>
                  <Text type="secondary">({file2Data.fileName})</Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select column from second file"
                    onChange={(value) => handleColumnSelect(value, 2)}
                    value={selectedColumns.file2Column}
                  >
                    {file2Data.columns.map(col => (
                      <Option key={col} value={col}>{col}</Option>
                    ))}
                  </Select>
                </Space>
              </Col>
            </Row>
          </StyledCard>

          <Space>
            <Button 
              type="primary" 
              onClick={handleMerge}
              disabled={!selectedColumns.file1Column || !selectedColumns.file2Column}
            >
              Merge Files
            </Button>
            {mergedData.length > 0 && (
              <Button onClick={handleDownload}>
                Download Merged File
              </Button>
            )}
          </Space>

          {mergedData.length > 0 && (
            <StyledCard title="Preview Merged Data" size="small">
              <Table
                dataSource={mergedData}
                columns={Object.keys(mergedData[0]).map(key => ({
                  title: key,
                  dataIndex: key,
                  key: key,
                }))}
                scroll={{ x: true }}
                pagination={{ pageSize: 5 }}
              />
            </StyledCard>
          )}
        </Space>
      )}
    </Container>
  );
};

export default ExcelMerger; 